import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useNavigateAlt } from "@features/utils";
import { HandThumbDownIcon, HeartIcon } from "@heroicons/react/16/solid";
import { Spinner } from "@radix-ui/themes";
import React from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface RelationshipImageThumbnailProps {
  image: ImagesType;
  isActive?: boolean;
  withBullet?: boolean;
  className?: string;
}

export const RelationshipImageThumbnail: React.FC<
  RelationshipImageThumbnailProps
> = ({
  image: imageProps,
  isActive = false,
  withBullet = false,
  className = "",
}) => {
  const image = useImage(imageProps.id).image || imageProps;
  const navigate = useNavigateAlt();
  const { site } = useParams();

  const getStatusIcon = () => {
    if (image.status === "pending") {
      return <Spinner size="1" className="text-slate-500" />;
    }
    if (image.status === "failed") {
      return (
        <span className="text-red-500 text-xs font-bold w-10 h-10 relative flex items-center justify-center">
          ✗
        </span>
      );
    }
    return null;
  };

  return (
    <button
      onClick={(event) => navigate(`/${site}/studio/${image.id}`, event)}
      className={twMerge(
        "relative w-10 h-10 rounded cursor-pointer hover:opacity-80 transition-opacity",
        isActive ? "ring-2 ring-blue-500 ring-offset-0" : "",
        className
      )}
      title={`${image.sub_type || image.type} - ${new Date(
        image.created_at
      ).toLocaleDateString()}`}
    >
      {/* Archived indicator */}
      {image.archived && (
        <HandThumbDownIcon className="z-10 w-3 h-3 absolute top-0.5 right-0.5 text-black dark:text-white" />
      )}

      {/* Favorite indicator */}
      {image.favorite && !image.archived && (
        <HeartIcon className="z-10 w-3 h-3 absolute top-0.5 right-0.5 text-red-500 drop-shadow-md" />
      )}

      {/* Active indicator dot */}
      {isActive && withBullet && (
        <div className="absolute bg-blue-500 rounded-full -top-3 right-0 mx-auto left-0 w-1.5 h-1.5 z-20"></div>
      )}

      {/* Image */}
      <img
        src={image.preview_url || image.url}
        alt=""
        className={twMerge(
          "w-full h-full object-cover rounded overflow-hidden",
          image.archived ? "opacity-25 grayscale" : ""
        )}
        onError={(e) => {
          // Fallback to main URL if preview fails
          const target = e.target as HTMLImageElement;
          if (target.src !== image.url) {
            target.src = image.url;
          }
        }}
      />

      {/* Status overlay */}
      {image.status !== "completed" && (
        <div
          className={twMerge(
            "absolute rounded top-0 left-0 z-10 w-full h-full bg-gray-100/50 dark:bg-gray-700/50 backdrop-blur flex items-center justify-center",
            image.archived ? "opacity-50" : ""
          )}
        >
          {getStatusIcon()}
        </div>
      )}
    </button>
  );
};
